import { useEffect, useRef, useState } from 'react'
import NavBar from '../components/NavBar'
import RoomListItem, { RoomRow } from '../components/RoomListItem'
import CreateRoomModal from '../components/CreateRoomModal'
import JoinByCodeModal from '../components/JoinByCodeModal'
import FriendListPanel from '../components/FriendListPanel'
import UserSearchModal from '../components/UserSearchModal'
import FriendRequestModal from '../components/FriendRequestModal'
import SettingsModal from '../components/SettingsModal'
import MyInfoModal from '../components/MyInfoModal'
import { AuthState, auth } from '../store/auth'
import { roomStore } from '../store/room'
import * as hub from '../hub/roomHub'
import { apiRoomList } from '../server/ServiceCall'
import '../css/App.css'

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768)
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>
    function onResize() {
      clearTimeout(timer)
      timer = setTimeout(() => setIsMobile(window.innerWidth < 768), 100)
    }
    window.addEventListener('resize', onResize)
    return () => { clearTimeout(timer); window.removeEventListener('resize', onResize) }
  }, [])
  return isMobile
}

interface Props {
  authState:    AuthState
  onLogout:     () => void
  onEnterRoom:  (roomCode: string) => void
  onNicknameChanged: (newNickname: string) => void
}

const POLL_MS = 8000

export default function Lobby({ authState, onLogout, onEnterRoom, onNicknameChanged }: Props) {
  const isMobile = useIsMobile()
  const [rooms,           setRooms]           = useState<RoomRow[]>([])
  const [gameTypeFilter,  setGameTypeFilter]   = useState('')
  const [loadingList,     setLoadingList]      = useState(false)
  const [listErr,         setListErr]          = useState('')
  const [showCreate,      setShowCreate]       = useState(false)
  const [showJoinCode,    setShowJoinCode]     = useState(false)
  const [showFriends,     setShowFriends]      = useState(false)
  const [showUserSearch,  setShowUserSearch]   = useState(false)
  const [showSettings,    setShowSettings]     = useState(false)  // SEQ=10110
  const [showMyInfo,      setShowMyInfo]       = useState(false)  // SEQ=10110
  const [joinPwdRoom,     setJoinPwdRoom]      = useState<string | null>(null)
  const [joinPwd,         setJoinPwd]          = useState('')
  const [joinErr,         setJoinErr]          = useState('')
  const [hubErr,          setHubErr]           = useState('')
  const pollRef                                = useRef<ReturnType<typeof setInterval> | null>(null)

  // ── SignalR 연결 시작 ──────────────────────────────────
  useEffect(() => {
    let cancelled = false

    async function connectHub() {
      try {
        await hub.startConnection(authState.token)
        if (!cancelled) setHubErr('')
      } catch (e: unknown) {
        if (!cancelled) setHubErr('실시간 연결 실패. 새로고침 해주세요.')
      }
    }
    connectHub()

    // ForceLogout 처리
    const onForceLogout = () => {
      auth.clear()
      window.dispatchEvent(new CustomEvent('session-expired'))
    }
    hub.on('ForceLogout', onForceLogout as (...a: unknown[]) => void)

    return () => {
      cancelled = true
      hub.off('ForceLogout', onForceLogout as (...a: unknown[]) => void)
    }
  }, [authState.token])

  // ── 룸 리스트 로드 ─────────────────────────────────────
  async function loadRooms() {
    setLoadingList(true)
    setListErr('')
    try {
      const r = await apiRoomList(authState.token, gameTypeFilter, 0, 50)
      if (r.resultCode === '00') {
        try {
          const parsed = JSON.parse(r.resultData)
          setRooms(Array.isArray(parsed) ? parsed : [])
        } catch {
          setRooms([])
        }
      } else {
        setListErr(r.resultMessage || '룸 목록 조회 실패')
      }
    } catch (e: unknown) {
      setListErr('네트워크 오류')
    } finally {
      setLoadingList(false)
    }
  }

  // 진입 시 + 필터 변경 시 로드
  useEffect(() => {
    loadRooms()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameTypeFilter])

  // 주기 polling
  useEffect(() => {
    pollRef.current = setInterval(loadRooms, POLL_MS)
    return () => { if (pollRef.current) clearInterval(pollRef.current) }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameTypeFilter, authState.token])

  // ── 로그아웃 ───────────────────────────────────────────
  async function handleLogout() {
    await hub.stopConnection()
    auth.clear()
    onLogout()
  }

  // ── 룸 리스트 클릭 입장 ────────────────────────────────
  async function handleRoomJoin(roomCode: string, isPrivate: boolean) {
    setJoinErr('')
    if (isPrivate) {
      setJoinPwdRoom(roomCode)
      return
    }
    await doJoin(roomCode, null)
  }

  async function doJoin(roomCode: string, password: string | null) {
    const result = await new Promise<{ ok: boolean; msg?: string }>(resolve => {
      const onDetail = (data: unknown) => {
        const d = data as Parameters<typeof roomStore.applyDetail>[0]
        if (d?.room && d?.members) roomStore.applyDetail(d)
        hub.off('RoomDetail', onDetail as (...a: unknown[]) => void)
        hub.off('Error',      onErr  as (...a: unknown[]) => void)
        resolve({ ok: true })
      }
      const onErr = (data: unknown) => {
        const d = data as { action?: string; msg?: string }
        if (d.action !== 'RoomJoin') return
        hub.off('RoomDetail', onDetail as (...a: unknown[]) => void)
        hub.off('Error',      onErr  as (...a: unknown[]) => void)
        resolve({ ok: false, msg: d.msg ?? '입장 실패' })
      }
      hub.on('RoomDetail', onDetail as (...a: unknown[]) => void)
      hub.on('Error',      onErr  as (...a: unknown[]) => void)

      hub.roomJoin(roomCode, password).catch(err => {
        hub.off('RoomDetail', onDetail as (...a: unknown[]) => void)
        hub.off('Error',      onErr  as (...a: unknown[]) => void)
        resolve({ ok: false, msg: (err as Error).message ?? '연결 오류' })
      })
    })

    if (result.ok) {
      onEnterRoom(roomCode)
    } else {
      setJoinErr(result.msg ?? '입장 실패')
    }
  }

  // 비공개 방 비번 입장
  async function handlePrivateJoin() {
    if (!joinPwdRoom) return
    await doJoin(joinPwdRoom, joinPwd || null)
    setJoinPwdRoom(null)
    setJoinPwd('')
  }

  // ── 랜덤 매칭 ─────────────────────────────────────────
  async function handleRandomJoin() {
    const available = rooms.filter(r => r.CURRENT_SEATS < r.MAX_SEATS && r.IS_PRIVATE === 0)
    if (available.length === 0) {
      setJoinErr('입장 가능한 공개 방이 없습니다. 방을 직접 만들어 보세요!')
      return
    }
    await doJoin(available[0].ROOM_CODE, null)
  }

  // ── 방 생성 완료 ───────────────────────────────────────
  function handleCreated(roomCode: string) {
    setShowCreate(false)
    onEnterRoom(roomCode)
  }

  // ── 코드 입장 완료 ─────────────────────────────────────
  function handleCodeJoined(roomCode: string) {
    setShowJoinCode(false)
    onEnterRoom(roomCode)
  }

  return (
    <div className="app-shell">
      <NavBar
        auth={authState}
        onLogout={handleLogout}
        onOpenSettings={() => setShowSettings(true)}
        onOpenMyInfo={() => setShowMyInfo(true)}
      />

      <div className="lobby-main">
        {hubErr && <p className="msg">{hubErr}</p>}

        {/* 필터 + 새로고침 */}
        <div className="lobby-toolbar">
          <select
            className="filter-select"
            value={gameTypeFilter}
            onChange={e => setGameTypeFilter(e.target.value)}
          >
            <option value="">전체 게임</option>
            <option value="CATAN">카탄</option>
            <option value="TICKET">티켓 투 라이드</option>
            <option value="RUMK">루미큐브</option>
          </select>
          <button className="btn-refresh" onClick={loadRooms} disabled={loadingList}>
            {loadingList ? <span className="spinner" /> : '새로고침'}
          </button>
        </div>

        {/* 룸 리스트: 데스크톱=테이블 / 모바일=카드 */}
        <div className="room-list-wrap">
          {listErr && <p className="msg">{listErr}</p>}
          {joinErr && <p className="msg">{joinErr}</p>}

          {rooms.length === 0 && !loadingList ? (
            <p className="muted room-empty">현재 열린 방이 없습니다.</p>
          ) : isMobile ? (
            /* 모바일 카드 리스트 */
            <div className="room-cards">
              {rooms.map(r => (
                <RoomListItem key={r.ROOM_CODE} room={r} onJoin={handleRoomJoin} cardMode />
              ))}
            </div>
          ) : (
            /* 데스크톱 테이블 */
            <table className="room-table">
              <thead>
                <tr>
                  <th>룸코드</th>
                  <th>방장</th>
                  <th>인원</th>
                  <th>생성</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rooms.map(r => (
                  <RoomListItem key={r.ROOM_CODE} room={r} onJoin={handleRoomJoin} />
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* 3 버튼 */}
        <div className="lobby-action-bar">
          <button className="btn-primary btn-lg" onClick={() => setShowCreate(true)}>
            방 만들기
          </button>
          <button className="btn-secondary btn-lg" onClick={handleRandomJoin}>
            랜덤 매칭
          </button>
          <button className="btn-secondary btn-lg" onClick={() => setShowJoinCode(true)}>
            코드로 입장
          </button>
        </div>
      </div>

      {/* 비공개 방 비번 인라인 모달 */}
      {joinPwdRoom && (
        <div className="modal-overlay" onClick={() => { setJoinPwdRoom(null); setJoinPwd('') }}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <h3 className="modal-title">비공개 방 입장</h3>
            <p className="muted" style={{ marginBottom: 12 }}>룸코드: <strong>{joinPwdRoom}</strong></p>
            <label className="modal-label">비밀번호</label>
            <input
              type="password"
              className="modal-input"
              placeholder="비밀번호 입력"
              value={joinPwd}
              onChange={e => setJoinPwd(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handlePrivateJoin()}
              autoFocus
            />
            <div className="modal-btns">
              <button className="btn-cancel" onClick={() => { setJoinPwdRoom(null); setJoinPwd('') }}>취소</button>
              <button className="btn-primary" onClick={handlePrivateJoin}>입장</button>
            </div>
          </div>
        </div>
      )}

      {showCreate   && <CreateRoomModal onClose={() => setShowCreate(false)}   onCreated={handleCreated}  />}
      {showJoinCode && <JoinByCodeModal onClose={() => setShowJoinCode(false)} onJoined={handleCodeJoined} />}

      {/* 친구 패널 (우측 슬라이드) */}
      {showFriends && (
        <div className="friend-sidebar">
          <FriendListPanel
            token={authState.token}
            myCode={authState.userCode}
            onOpenSearch={() => { setShowFriends(false); setShowUserSearch(true) }}
          />
          <button
            className="friend-sidebar-close"
            onClick={() => setShowFriends(false)}
            title="닫기"
          >✕</button>
        </div>
      )}

      {/* 유저 검색 모달 */}
      {showUserSearch && (
        <UserSearchModal
          token={authState.token}
          myCode={authState.userCode}
          onClose={() => setShowUserSearch(false)}
        />
      )}

      {/* 친구 요청 실시간 알림 */}
      <FriendRequestModal token={authState.token} />

      {/* 친구 패널 토글 버튼 (우하단 FAB) */}
      <button
        className="friend-fab"
        onClick={() => setShowFriends(v => !v)}
        title="친구 목록"
      >
        👥
      </button>

      {/* SEQ=10110 STEP=4 — 설정 / 내정보 모달 */}
      {showSettings && <SettingsModal onClose={() => setShowSettings(false)} />}
      {showMyInfo && (
        <MyInfoModal
          authState={authState}
          onNicknameChanged={onNicknameChanged}
          onClose={() => setShowMyInfo(false)}
        />
      )}
    </div>
  )
}
